// Receipt-confirmation form for a single order line. Shared by the owner Receiving view and the
// scoped canner view. Handles field entry + photo/COA upload (base64 → Blobs) and calls /receive.
import { useState } from "react";
import type { OrderLine, Receipt } from "@shared/types.ts";
import { api, photoSrc } from "../lib/api.ts";
import { LabeledInput } from "./ui.tsx";
import { convertMass, isMassUom, UOM_OPTIONS } from "@shared/engine.ts";
import { todayISO, mass } from "../lib/format.ts";

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export function ReceiveForm({ businessId, orderId, line, token, massUnit = "lb", onDone }: {
  businessId: string; orderId: string; line: OrderLine; token?: string; massUnit?: string; onDone: () => void;
}) {
  const prev = line.received;
  const [r, setR] = useState<Receipt>({
    dateReceived: prev?.dateReceived || todayISO(),
    lotNumber: prev?.lotNumber || "",
    expiryDate: prev?.expiryDate || "",
    uom: prev?.uom || line.uom || (massUnit === "kg" ? "Kilograms" : "Pounds"),
    qtyGood: prev?.qtyGood ?? line.qtyOrdered ?? Math.round(line.toOrderLbs * 100) / 100,
    qtyDamaged: prev?.qtyDamaged ?? 0,
    coa: prev?.coa ?? false,
    notes: prev?.notes || "",
    photos: prev?.photos ? [...prev.photos] : [],
  });
  const [addToStock, setAddToStock] = useState(true);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const set = <K extends keyof Receipt>(k: K, v: Receipt[K]) => setR((s) => ({ ...s, [k]: v }));

  const onFiles = async (files: FileList | null) => {
    if (!files || !files.length) return;
    setUploading(true); setErr(null);
    try {
      for (const f of Array.from(files)) {
        const dataUrl = await fileToDataUrl(f);
        const res = await api.upload(businessId, dataUrl, token);
        setR((s) => ({ ...s, photos: [...s.photos, res.key] }));
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      await api.receive({ businessId, orderId, lineId: line.id, receipt: r, addToStock }, token);
      onDone();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-lg bg-paper border border-edge/70 p-3 text-sm">
        <div className="font-medium">{line.ingredient}</div>
        <div className="text-muted text-xs mt-0.5">
          {line.supplier || "—"}
          {line.poNumber ? ` · PO ${line.poNumber}` : ""}
          {line.qtyOrdered ? ` · ordered ${line.qtyOrdered} ${line.uom || massUnit}` : line.toOrderLbs ? ` · ordered ~${line.toOrderLbs} ${massUnit}` : ""}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <LabeledInput label="Date received"><input type="date" className="input" value={r.dateReceived} onChange={(e) => set("dateReceived", e.target.value)} /></LabeledInput>
        <LabeledInput label="Lot number"><input className="input" value={r.lotNumber} onChange={(e) => set("lotNumber", e.target.value)} placeholder="e.g. B053834" /></LabeledInput>
        <LabeledInput label="Expiry date"><input type="date" className="input" value={r.expiryDate} onChange={(e) => set("expiryDate", e.target.value)} /></LabeledInput>
        <LabeledInput label="Unit of measure" hint="How it was received — converted to stock automatically">
          <select className="input" value={r.uom} onChange={(e) => set("uom", e.target.value)}>
            {r.uom && !UOM_OPTIONS.includes(r.uom) && <option value={r.uom}>{r.uom}</option>}
            {UOM_OPTIONS.map((u) => <option key={u} value={u}>{u}</option>)}
          </select>
        </LabeledInput>
        <LabeledInput label="Qty received (good)"><input type="number" step="any" className="input" value={r.qtyGood ?? ""} onChange={(e) => set("qtyGood", e.target.value === "" ? undefined : Number(e.target.value))} /></LabeledInput>
        <LabeledInput label="Qty damaged"><input type="number" step="any" className="input" value={r.qtyDamaged ?? ""} onChange={(e) => set("qtyDamaged", e.target.value === "" ? undefined : Number(e.target.value))} /></LabeledInput>
      </div>
      {addToStock && (Number(r.qtyGood) || 0) > 0 && isMassUom(r.uom) && (
        <p className="text-xs text-muted -mt-2">Adds <span className="font-medium text-ink">{mass(convertMass(Number(r.qtyGood) || 0, r.uom, massUnit), massUnit)}</span> to on-hand stock{(r.uom || "").trim().toLowerCase() !== massUnit ? ` (from ${r.qtyGood} ${r.uom})` : ""}.</p>
      )}

      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={!!r.coa} onChange={(e) => set("coa", e.target.checked)} />
        COA / COC received & on file
      </label>

      <LabeledInput label="Notes"><textarea className="input" rows={2} value={r.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Condition, discrepancies, etc." /></LabeledInput>

      <div>
        <span className="label">Photographic evidence</span>
        <div className="flex flex-wrap gap-2 mb-2">
          {r.photos.map((k) => (
            <div key={k} className="relative group">
              <a href={photoSrc(k, token)} target="_blank" rel="noreferrer">
                <img src={photoSrc(k, token)} alt="evidence" className="w-20 h-20 object-cover rounded-lg border border-edge" />
              </a>
              <button onClick={() => setR((s) => ({ ...s, photos: s.photos.filter((p) => p !== k) }))}
                className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-ink text-white text-xs opacity-0 group-hover:opacity-100">✕</button>
            </div>
          ))}
          <label className="w-20 h-20 rounded-lg border border-dashed border-edge grid place-items-center text-muted text-xs cursor-pointer hover:border-brand hover:text-brand">
            {uploading ? "…" : "+ Photo"}
            <input type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={(e) => onFiles(e.target.files)} />
          </label>
        </div>
      </div>

      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={addToStock} onChange={(e) => setAddToStock(e.target.checked)} />
        Add the good quantity to on-hand stock
      </label>

      {err && <div className="text-danger text-sm">{err}</div>}
      <div className="flex justify-end gap-2 pt-1">
        <button className="btn-primary" disabled={busy || uploading} onClick={submit}>{busy ? "Saving…" : prev?.confirmedAt ? "Update confirmation" : "Confirm receipt"}</button>
      </div>
    </div>
  );
}
